import { useState, useEffect } from 'react';

interface CountryData {
    countryCode: string | null;
    loading: boolean;
    error: Error | null;
    isOnline: boolean;
}

// Cache key for localStorage
const COUNTRY_CACHE_KEY = 'user_country_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

interface CachedCountryData {
    countryCode: string;
    timestamp: number;
}

export function useUserCountry(): CountryData {
    const [countryCode, setCountryCode] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

    useEffect(() => {
        const getCachedCountry = (): string | null => {
            try {
                const cached = localStorage.getItem(COUNTRY_CACHE_KEY);
                if (cached) {
                    const data: CachedCountryData = JSON.parse(cached);
                    const isExpired = Date.now() - data.timestamp > CACHE_DURATION;
                    if (!isExpired && data.countryCode) {
                        return data.countryCode;
                    }
                }
            } catch (err) {
                console.warn('Failed to read cached country:', err);
            }
            return null;
        };

        const setCachedCountry = (country: string): void => {
            try {
                const data: CachedCountryData = {
                    countryCode: country,
                    timestamp: Date.now()
                };
                localStorage.setItem(COUNTRY_CACHE_KEY, JSON.stringify(data));
            } catch (err) {
                console.warn('Failed to cache country:', err);
            }
        };

        const clearCachedCountry = (): void => {
            try {
                localStorage.removeItem(COUNTRY_CACHE_KEY);
                console.log('Country cache cleared due to offline event');
            } catch (err) {
                console.warn('Failed to clear cached country:', err);
            }
        };

        const fetchUserCountry = async () => {
            try {
                setLoading(true);
                setError(null);

                // Check cache first
                const cached = getCachedCountry();
                if (cached) {
                    setCountryCode(cached);
                    setLoading(false);
                    return;
                }

                // Try multiple services for better reliability
                const services = [
                    {
                        url: 'https://ipapi.co/country_code/',
                        parser: (text: string) => text.trim().toUpperCase()
                    },
                    {
                        url: 'https://api.country.is/',
                        parser: (text: string) => {
                            const data = JSON.parse(text);
                            return data.country?.trim().toUpperCase();
                        }
                    },
                    {
                        url: 'https://ipinfo.io/country',
                        parser: (text: string) => text.trim().toUpperCase()
                    }
                ];

                let countryResult: string | null = null;

                for (const service of services) {
                    try {
                        const response = await fetch(service.url, {
                            method: 'GET',
                            headers: {
                                'Accept': service.url.includes('country.is') ? 'application/json' : 'text/plain',
                            },
                            // Add timeout
                            signal: AbortSignal.timeout(5000)
                        });

                        if (response.ok) {
                            const result = await response.text();
                            const parsedResult = service.parser(result);

                            // Validate ISO2 format (2 letters)
                            if (parsedResult && parsedResult.length === 2 && /^[A-Z]{2}$/.test(parsedResult)) {
                                countryResult = parsedResult;
                                break;
                            }
                        }
                    } catch (serviceError) {
                        console.warn(`Failed to fetch from ${service.url}:`, serviceError);
                        continue;
                    }
                }

                if (countryResult) {
                    setCountryCode(countryResult);
                    setCachedCountry(countryResult);
                } else {
                    setError(new Error('Unable to determine country from any service'));
                }
            } catch (err) {
                setError(err instanceof Error ? new Error(`Failed to fetch country: ${err.message}`) : new Error('Failed to fetch country'));
            } finally {
                setLoading(false);
            }
        };

        // Set up online/offline event listeners
        const handleOffline = () => {
            setIsOnline(false);
            clearCachedCountry();

        };

        const handleOnline = () => {
            setCountryCode(null);
            setIsOnline(true);
            // Re-fetch country when coming back online
            fetchUserCountry();
        };

        // Add event listeners
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        // Initial fetch
        fetchUserCountry();

        // Cleanup event listeners
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, []);

    return { countryCode, loading, error, isOnline };
}
